import React, { useEffect } from 'react';
import { X, AlertCircle } from 'lucide-react';
import IndustryGrid from './shared/IndustryGrid';
import IndustryBadge from './shared/IndustryBadge';
import useIndustryManagement from '../hooks/useIndustryManagement';
import { INDUSTRIES } from '../constants/industries';

/**
 * Simplified User Industry Manager Component
 * No constraints - everyone can have any industries
 */
const UserIndustryManager = ({ user, onClose, onUpdate }) => {
  const {
    context,
    selectedIndustries,
    isLoading,
    isSaving,
    fetchContext,
    updateIndustries,
    toggleIndustry
  } = useIndustryManagement();

  useEffect(() => {
    if (user) {
      fetchContext(user.id);
    }
  }, [user, fetchContext]);

  const handleSave = async () => {
    const result = await updateIndustries(user.id, selectedIndustries);
    if (result.success) {
      onUpdate();
      onClose();
    }
  };

  if (!user) return null;

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!context) return null;

  const addedIndustries = selectedIndustries.filter(
    i => !context.current_industries.includes(i)
  );
  const removedIndustries = context.current_industries.filter(
    i => !selectedIndustries.includes(i)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex justify-between items-center sticky top-0">
          <div>
            <h3 className="text-xl font-bold text-white">Manage Industries</h3>
            <p className="text-indigo-100 text-sm mt-1">
              {context.full_name} - {context.role}
            </p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Simplified Context - Just show if in team */}
          {context.team_info && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div>
                  <h4 className="font-semibold text-blue-900">
                    {context.team_info.is_lead ? 'Leading Team' : 'Team Member'}
                  </h4>
                  <p className="text-sm text-blue-700 mt-1">
                    {context.team_info.team_name}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Current Industries */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">Current Industries:</h4>
            {context.current_industries.length === 0 ? (
              <p className="text-gray-500 text-sm">No industries assigned</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {context.current_industries.map(industry => (
                  <IndustryBadge key={industry} industry={industry} variant="default" />
                ))}
              </div>
            )}
          </div>

          {/* Industry Selection Grid - All industries available */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">
                Select Industries ({selectedIndustries.length} selected):
              </label>
              {selectedIndustries.length > 0 && (
                <button
                  onClick={() => selectedIndustries.forEach(i => toggleIndustry(i))}
                  className="text-xs text-gray-600 hover:text-gray-800"
                >
                  Clear All
                </button>
              )}
            </div>

            <IndustryGrid
              industries={INDUSTRIES}
              selectedIndustries={selectedIndustries}
              onToggle={toggleIndustry}
              disabledIndustries={[]} // No constraints - all available
              currentIndustries={context.current_industries}
              columns={3}
            />
          </div>

          {/* Change Summary */}
          {(addedIndustries.length > 0 || removedIndustries.length > 0) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Changes Summary:</h4>
              <div className="space-y-2 text-sm">
                {addedIndustries.length > 0 && (
                  <div>
                    <span className="text-green-700 font-medium">Adding: </span>
                    <span className="text-green-600">{addedIndustries.join(', ')}</span>
                  </div>
                )}
                {removedIndustries.length > 0 && (
                  <div>
                    <span className="text-red-700 font-medium">Removing: </span>
                    <span className="text-red-600">{removedIndustries.join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Optional warning for no industries */}
          {selectedIndustries.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                <p className="text-sm text-yellow-800">
                  Consider assigning at least one industry for better job visibility.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between gap-3 sticky bottom-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserIndustryManager;
